import { ol_Feature } from "@omnia/exports/openlayers"
import { defineDirective, fields, FieldsMap, IFieldsMapContainerContext } from "../../../omnia"
import { ISoilService } from "../../../types"
import { AllocationHistoryAction, MapAllocationContext } from "../context"
import { AnalysisType } from "../types"
import { GlobalHealthySoilsAssessmentVM } from "../viewmodel/hsa-vm"
import { GlobalNitrogenAnalysisVM } from "../viewmodel/nitrogen-vm"
import { GlobalSoilAnalysisVM } from "../viewmodel/soilanalysis-vm"
import { AnalysisLayer, IAnalysisLayerData } from "./analysis-layer"

import { Highlight } from "./highlight"

export class MapCtrl {

    static create(): ng.IDirective {
        return defineDirective(MapCtrl, "app/soil/dashboard/global-map/map/map.html", {
            context: "="
        });
    }

    public context: MapAllocationContext;

    private map: FieldsMap;
    private analysisLayer: AnalysisLayer;

    private highlight: Highlight;

    static $inject = ["$scope", "OmniaSoilService", "OmniaFieldsMapContext"];

    constructor(private $scope: ng.IScope, private svc: ISoilService, private mapContainer: IFieldsMapContainerContext) {

        $scope.$watch("vm.context.analysisType", () => this.setup());
        $scope.$watch("vm.context.selectedAnalysis", () => this.selectedAnalysisChanged());

    }

    $onDestroy() {

        if (this.highlight) {
            this.highlight.dispose();
        }

    }

    get ready(): boolean {
        return !!this.context
    }

    private setup() {
        if (this.context) {
            this.setupMap();
            this.setupLayers();
            if (!this.highlight) {
                this.highlight = new Highlight(this.map);
                this.highlight.activate();
            }
        }
    }

    private setupMap() {

        if (!this.map) {
            this.map = new fields.map.FieldsMap(this.mapContainer, {
                showDisabledFields: true,
                viewDefaultExtent: true
            });
            this.map.enableSelection = false;
        }

    }

    private setupLayers() {

            if (!this.analysisLayer) {
                this.analysisLayer = new AnalysisLayer(this.map.getMap(), (f: ol_Feature) => {
                    return (this.context.selectedAnalysis && f.get("data").analysis !== this.context.selectedAnalysis) ||
                        (!this.context.selectedAnalysis && f.get("data").analysis);
                });

            this.analysisLayer.watchSelection(f => this.featureSelectionChanged(f));

            this.map.fieldsLayer.selectable = false;
            this.map.croppingLayer.selectable = false;

            this.map.addLayer(this.analysisLayer, true);
            this.map.setSelectableLayers([this.analysisLayer]);

            this.analysisLayer.opacity = 100;
            this.context.analysisLayer = this.analysisLayer;
        }

        if (this.context.analysisType === AnalysisType.Soil && this.context.analysis) {

            this.analysisLayer.setSoilAnalysis(this.context.svc.fields,
                this.context.soilAnalysisLayers,
                this.context.soilTypeLayers,
                <GlobalSoilAnalysisVM[]>this.context.analysis,
                this.context.showOnlyFields);

        } else if (this.context.analysisType === AnalysisType.Nitrogen) {

            this.analysisLayer.setNitrogenAnalysis(this.context.svc.cropping,
                <GlobalNitrogenAnalysisVM[]>this.context.analysis);

        } else if (this.context.analysisType === AnalysisType.HSA) {

            this.analysisLayer.setHealthySoilsAssessment(this.context.svc.fields,
                <GlobalHealthySoilsAssessmentVM[]>this.context.analysis);

        }

        this.context.refreshFeatureSelection = (d: IAnalysisLayerData) => {
            var features = this.analysisLayer.findFeatures(f => {
                var data = <IAnalysisLayerData>f.get("data");
                return data === d;
            });

            this.analysisLayer.silentUnselectFeature(features[0]);
        };

        this.context.refreshFeatures = datas => {
            var features = this.analysisLayer.findFeatures(f => {
                var data = <IAnalysisLayerData>f.get("data");
                return datas.indexOf(data) !== -1;
            });

            features.forEach(f => f.changed());
        };

        this.context.setupLayers = () => {
            this.setupLayers();
        };

    }

    private featureSelectionChanged(f: ol_Feature[]) {
        if (!this.context.updatingSelection && this.context.selectedAnalysis) {

            var datas = f.map(ff => <IAnalysisLayerData>ff.get("data"));
            var analysis = this.context.selectedAnalysis;

            var keptAllocations = analysis.allocations.filter(a => analysis.checkAllocationInDatas(a, datas));
            var deletedAllocations = analysis.allocations.filter(a => keptAllocations.indexOf(a) === -1);
            deletedAllocations.filter(a => !!a.layerData).forEach(a => {
                this.context.allocationHistory.push({
                    action: AllocationHistoryAction.Unallocate,
                    analysis: analysis,
                    layerData: a.layerData
                });
                a.layerData.analysis = null;
            });

            analysis.allocations = keptAllocations;

            var allocationsAdded = datas.filter(d => !analysis.checkDataInAllocations(d));
            allocationsAdded.forEach(a => {
                // Remove existing analysis allocation if exists on feature
                if (a.analysis) {
                    this.context.allocationHistory.push({
                        action: AllocationHistoryAction.Unallocate,
                        analysis: a.analysis,
                        layerData: a
                    });

                    a.analysis.unallocFeature(a);
                }

                // Alloc analysis
                this.context.allocationHistory.push({
                    action: AllocationHistoryAction.Allocate,
                    analysis: analysis,
                    layerData: a
                });

                analysis.alloc(a);
                a.analysis = analysis;
            });

            this.$scope.$apply();
            this.analysisLayer.refresh();

        } else if (!this.context.updatingSelection && f.length === 1) {
            var analysis = (<IAnalysisLayerData>f[0].get("data")).analysis;
            if (analysis) {
                this.context.selectedAnalysis = analysis;
                this.$scope.$apply();
            }
        }
    }

    private selectedAnalysisChanged() {

        if (!this.context) return;

        var a = this.context.selectedAnalysis;

        this.context.updatingSelection = true;

        this.analysisLayer.clearSelection();

        if (a) {
            var features = this.analysisLayer.findFeatures(f => {
                var data = <IAnalysisLayerData>f.get("data");
                return data && data.analysis && data.analysis === a;
            });
            this.analysisLayer.selectFeatures(features);

            this.map.viewExtentForDataItems(features.map(f => f.get("data")));
        }

        this.context.updatingSelection = false;
    }


}
